import React from 'react';
import { IoClose } from 'react-icons/io5';
import {
  industryOptions,
  getTemplateOptionsByMode,
  customTemplateOptions,
  type TemplateMode,
} from './templateOptions';

interface FormPopoverProps {
  selectedIndustry: string;
  onSelectIndustry: (industry: string) => void;
  selectedTemplate: string;
  onSelectTemplate: (template: string) => void;
  templateMode: TemplateMode;
  onChangeTemplateMode: (mode: TemplateMode) => void;
  onClose: () => void;
  onSubmit: () => void;
}

/**
 * 居中浮层 - 行业/模板选择 + 确认整理纪要
 * 点击"整理纪要"按钮后直接显示
 */
const FormPopover: React.FC<FormPopoverProps> = ({
  selectedIndustry,
  onSelectIndustry,
  selectedTemplate,
  onSelectTemplate,
  templateMode,
  onChangeTemplateMode,
  onClose,
  onSubmit,
}) => {
  const templateOptions = getTemplateOptionsByMode(templateMode);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="w-[460px] bg-white rounded-2xl shadow-2xl border border-[#E9E9E7] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E9E9E7]">
          <div>
            <div className="text-base font-semibold text-[#37352F]">整理纪要</div>
            <div className="text-xs text-[#91918E] mt-1">确认后将停止录音并生成会议纪要</div>
          </div>
          <button
            className="w-7 h-7 flex items-center justify-center text-[#91918E] hover:text-[#37352F] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
            onClick={onClose}
          >
            <IoClose className="w-4 h-4" />
          </button>
        </div>

      {/* 内容 */}
      <div className="px-6 py-5 space-y-5">
        {/* 行业选择 */}
        <div>
          <div className="text-sm font-semibold text-[#37352F] mb-3">行业</div>
          <div className="flex flex-wrap gap-2">
            {industryOptions.map((industry) => (
              <span
                key={industry}
                className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                  selectedIndustry === industry
                    ? 'bg-black text-white'
                    : 'bg-[#F7F7F5] text-[#73726E] hover:bg-[#EDEDEC]'
                }`}
                onClick={() => onSelectIndustry(industry)}
              >
                {industry}
              </span>
            ))}
          </div>
        </div>

        {/* 模板选择 */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-semibold text-[#37352F]">模板</span>
            <div className="flex border-b border-[#E9E9E7]">
              {(['classic', 'scenario', 'custom'] as const).map((mode) => (
                <button
                  key={mode}
                  className={`text-xs pb-1.5 px-2 -mb-[1px] transition-colors cursor-pointer ${
                    templateMode === mode
                      ? 'font-bold border-b-[1.5px] border-black text-[#37352F]'
                      : 'text-[#91918E] hover:text-[#73726E]'
                  }`}
                  onClick={() => onChangeTemplateMode(mode)}
                >
                  {{ classic: '经典', scenario: '场景', custom: '自定义' }[mode]}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {templateMode === 'custom' && customTemplateOptions.length === 0 ? (
              <span className="text-xs text-[#91918E]">暂无自定义模板</span>
            ) : (
              templateOptions.map((template) => (
                <span
                  key={template.value}
                  className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                    selectedTemplate === template.value
                      ? 'bg-black text-white'
                      : 'bg-[#F7F7F5] text-[#73726E] hover:bg-[#EDEDEC]'
                  }`}
                  onClick={() => onSelectTemplate(template.value)}
                >
                  {template.label}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="px-6 py-4 border-t border-[#E9E9E7] flex justify-end gap-3">
        <button
          className="px-5 py-2.5 text-sm text-[#73726E] hover:bg-[#F7F7F5] rounded-xl transition-colors cursor-pointer"
          onClick={onClose}
        >
          取消
        </button>
        <button
          className="px-6 py-2.5 text-sm font-medium bg-black text-white rounded-xl hover:bg-zinc-800 transition-colors cursor-pointer"
          onClick={onSubmit}
        >
          确认提交
        </button>
      </div>
      </div>
    </div>
  );
};

export default FormPopover;
